// Author registry for E-E-A-T and AdSense compliance.
// Each article references an authorId; ArticleJsonLd emits Person schema
// when an author is resolved. Keep bios sajt-specific to avoid network footprint.

export interface Author {
  id: string;
  slug: string;
  name: string;
  role: string;
  shortBio: string; // 1–2 sentences for byline tooltips/cards
  bio: string; // Long-form bio shown on /author/[slug]
  expertise: string[]; // Max 5 areas, displayed on author page
  avatar?: string; // Public URL or path; placeholder used if missing
  email?: string;
  sameAs?: string[]; // External profiles (LinkedIn, personal site). Keep short and verifiable.
  joinedYear?: number;
}

// IMPORTANT: This bio is intentionally specific to Life Basics Guide's
// life-skills angle. Do NOT copy this verbatim to other sites in the network.
export const authors: Author[] = [
  {
    id: "carl-molner",
    slug: "carl-molner",
    name: "Carl S. Molner",
    role: "Founder & Editor",
    shortBio:
      "Founded Life Basics Guide after years of watching capable adults feel embarrassed about practical things no one had taught them.",
    bio:
      "Carl started Life Basics Guide because he kept meeting capable, smart adults who felt quietly ashamed about not knowing things they assumed everyone else already knew — how to read a tenancy agreement, why the boiler keeps losing pressure, what to actually do when a tax letter arrives. He grew up moving between countries and learned most of these things the slow way: by getting it wrong, asking awkward questions, and writing down what worked. That habit of plain-language note-taking eventually became the editorial voice of this site.\n\nHe edits every article personally and works with subject reviewers — qualified tradespeople, accountants, and case workers — for anything where bad guidance could cost real money or cause real harm. The goal is never to sound clever. The goal is for someone reading at 11pm, slightly panicked, to leave the page knowing exactly what to do next.",
    expertise: [
      "Personal finance basics",
      "Renting and housing admin",
      "Everyday home maintenance",
      "Bureaucracy and paperwork",
      "Adult life logistics",
    ],
    sameAs: [], // Intentionally empty — separated bio strategy across the network.
    joinedYear: 2024,
  },
];

const authorById = new Map(authors.map((a) => [a.id, a]));
const authorBySlug = new Map(authors.map((a) => [a.slug, a]));

export function getAuthorById(id?: string): Author | undefined {
  if (!id) return undefined;
  return authorById.get(id);
}

export function getAuthorBySlug(slug: string): Author | undefined {
  return authorBySlug.get(slug);
}

export function getDefaultAuthor(): Author {
  return authors[0];
}

export function resolveArticleAuthor(authorId?: string): Author {
  return getAuthorById(authorId) ?? getDefaultAuthor();
}
